import { useState } from "react";
import { toast } from "sonner";
import { Check, Undo2, AlertTriangle } from "lucide-react";
import { http, errMsg } from "../../lib/api";

export const diff = (a, b) => {
  const x = a.split(/(\s+)/), y = b.split(/(\s+)/);
  const m = x.map(() => Array(y.length + 1).fill(0));
  m.push(Array(y.length + 1).fill(0));
  for (let i = x.length - 1; i >= 0; i--) for (let j = y.length - 1; j >= 0; j--) m[i][j] = x[i] === y[j] ? m[i + 1][j + 1] + 1 : Math.max(m[i + 1][j], m[i][j + 1]);
  const out = [];
  let i = 0, j = 0;
  while (i < x.length || j < y.length) {
    if (i < x.length && j < y.length && x[i] === y[j]) { out.push(["=", x[i]]); i++; j++; }
    else if (i < x.length && (j >= y.length || m[i + 1][j] >= m[i][j + 1])) { out.push(["-", x[i]]); i++; }
    else { out.push(["+", y[j]]); j++; }
  }
  return out;
};

const STATUS = { approved: ["Approved", "text-emerald-600"], undone: ["Original restored", "text-sky-600"], changed: ["Text has since changed — edit it by hand", "text-amber-600"] };

const Change = ({ c, i, k, act }) => {
  const fact = /fact|outdated/i.test(c.reason || "");
  return (
    <div data-testid={`import-change-${k}-${i}`} className="py-2 border-t border-stone-100 text-sm leading-relaxed">
      {diff(c.original, c.fixed).map(([t, w], n) => t === "=" ? <span key={n}>{w}</span>
        : t === "-" ? <del key={n} className="bg-red-50 text-red-600">{w}</del> : <ins key={n} className="bg-emerald-50 text-emerald-700 no-underline">{w}</ins>)}
      {c.reason && <span className="ml-2 text-[10px] uppercase tracking-wider text-stone-400">{c.reason}</span>}
      <div className="mt-1.5 flex items-center gap-2">
        {c.status ? <span data-testid={`import-change-status-${k}-${i}`} className={`text-[11px] font-semibold ${STATUS[c.status][1]}`}>{STATUS[c.status][0]}</span> : (<>
          <button data-testid={`import-change-approve-${k}-${i}`} onClick={() => act([{ key: k, i, action: "approve" }])} className="inline-flex items-center gap-1 rounded-sm bg-emerald-600 px-2.5 py-1 text-[11px] font-bold text-white"><Check size={12} /> Approve</button>
          <button data-testid={`import-change-undo-${k}-${i}`} onClick={() => (!fact || window.confirm("This fix corrects a fact — your original may be outdated (e.g. an old phone number or price). Restore the original anyway?")) && act([{ key: k, i, action: "undo" }])} className="inline-flex items-center gap-1 rounded-sm border border-stone-300 px-2.5 py-1 text-[11px] font-bold text-stone-600 hover:bg-stone-100"><Undo2 size={12} /> Undo</button>
          {fact && <span className="inline-flex items-center gap-1 text-[11px] text-amber-600"><AlertTriangle size={12} /> fact fix — original may be outdated</span>}
        </>)}
      </div>
    </div>
  );
};

export const ImportChanges = ({ imp, kitId, reload }) => {
  const [open, setOpen] = useState(null);
  const act = async (items) => {
    try {
      const { data } = await http.put(`/kits/${kitId}/import-fixes`, { items });
      if (data.changed) toast.warning("Some text has since changed — edit it by hand");
      else toast.success(data.undone ? "Original wording restored" : "Approved");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!imp.done) return null;
  if (!imp.changes) return <div data-testid="import-changes-none" className="mt-2 text-xs text-stone-400">Changes not recorded for this import.</div>;
  const titles = imp.titles || {};
  const keys = [...Object.keys(titles).filter((k) => imp.changes[k]?.length || (imp.kept || []).includes(titles[k]) || (imp.written || []).includes(titles[k])),
    ...Object.keys(imp.changes).filter((k) => !titles[k] && imp.changes[k]?.length)];
  const pending = Object.values(imp.changes).flat().filter((c) => !c.status).length;
  return (
    <div data-testid="import-changes" className="mt-3 divide-y divide-stone-100 border-t border-stone-100">
      <div data-testid="import-fixes-pending" className={`py-2 text-xs font-semibold ${pending ? "text-amber-600" : "text-emerald-600"}`}>{pending ? `${pending} fix${pending === 1 ? "" : "es"} still to review — Publish unlocks when every fix is approved or undone` : "All fixes reviewed"}</div>
      {keys.map((k) => {
        const ch = imp.changes[k] || [];
        const isNew = (imp.written || []).includes(titles[k]);
        return (
          <div key={k}>
            <button data-testid={`import-changes-section-${k}`} onClick={() => setOpen(open === k ? null : k)} disabled={!ch.length} className="w-full flex items-center gap-3 py-2 text-left text-sm disabled:cursor-default">
              <span className="text-[#2B2A26]">{titles[k] || (k === "other" ? "Other fixes (not linked to one section)" : `Other fixes (${k})`)}</span>
              <span className={`ml-auto text-[10px] font-bold uppercase tracking-wider ${isNew ? "text-sky-600" : ch.length ? "text-amber-600" : "text-emerald-600"}`}>{isNew ? "New — written for your standard" : ch.length ? `${ch.length} fix${ch.length === 1 ? "" : "es"}` : "Your wording, unchanged"}</span>
            </button>
            {open === k && <div className="pb-3">
              {ch.some((c) => !c.status) && <button data-testid={`import-approve-all-${k}`} onClick={() => act(ch.map((c, i) => [c, i]).filter(([c]) => !c.status).map(([, i]) => ({ key: k, i, action: "approve" })))} className="mb-1 text-[11px] font-bold text-emerald-700 hover:underline">Approve all in this section</button>}
              {ch.map((c, i) => <Change key={i} c={c} i={i} k={k} act={act} />)}
            </div>}
          </div>
        );
      })}
    </div>
  );
};
